(() => {
  const DEFAULTS = {
    anim: 'animate__fadeInUp',
    duration: 700,
    delay: 0,
    once: true,
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',
  };

  const toInt = (v, fallback) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fallback;
  };

  const getBool = (v, fallback) => {
    if (v === undefined || v === null || v === '') return fallback;
    return v === 'true' || v === '1';
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // --- helpers for "class mode" (transition-based)
  const setTransitionVars = (el) => {
    const duration = toInt(el.dataset.animDuration, DEFAULTS.duration);
    const delay = toInt(el.dataset.animDelay, DEFAULTS.delay);
    el.style.setProperty('--aq-intro-dur', `${duration}ms`);
    el.style.setProperty('--aq-intro-delay', `${delay}ms`);
  };

  const prepareElement = (el) => {
    if (el.dataset.animPrepared === '1') return;
    el.dataset.animPrepared = '1';

    // Transition/class mode: don't touch opacity here (CSS controls it)
    if (el.dataset.animClass) {
      setTransitionVars(el);
      return;
    }

    // Animate.css mode
    el.style.opacity = '0';

    const wc = (el.dataset.animWillchange || '').trim();
    el.style.willChange = wc ? wc : 'transform, opacity';
  };

  const playAnimation = (el) => {
    const anim = el.dataset.anim || DEFAULTS.anim;
    const duration = toInt(el.dataset.animDuration, DEFAULTS.duration);
    const delay = toInt(el.dataset.animDelay, DEFAULTS.delay);

    // vars for animate.css + inline (some browsers read one better than the other)
    el.style.setProperty('--animate-duration', `${duration}ms`);
    el.style.setProperty('--animate-delay', `${delay}ms`);
    el.style.animationDuration = `${duration}ms`;
    el.style.animationDelay = `${delay}ms`;

    // keep hidden until animation actually starts (after delay)
    el.style.opacity = '0';

    // reset for replay safety
    el.classList.remove('animate__animated');
    el.classList.remove(anim);
    void el.offsetWidth;

    const onStart = () => {
      el.style.opacity = '';
      el.removeEventListener('animationstart', onStart);
    };
    el.addEventListener('animationstart', onStart, { once: true });

    if (!delay) el.style.opacity = '';

    el.classList.add('animate__animated', anim);
  };

  const clearAnimationClasses = (el) => {
    // Transition/class mode: remove class and let CSS hide it
    if (el.dataset.animClass) {
      el.classList.remove(el.dataset.animClass);
      return;
    }

    const anim = el.dataset.anim || DEFAULTS.anim;
    el.classList.remove('animate__animated', anim);
    el.style.setProperty('--animate-delay', '');
    el.style.setProperty('--animate-duration', '');
  };

  const collectTargets = () => {
    const singles = Array.from(document.querySelectorAll('[data-anim]'));
    const groups = Array.from(document.querySelectorAll('[data-anim-child]'));
    const groupChildren = groups.flatMap((group) =>
      Array.from(group.querySelectorAll('[data-anim]'))
    );
    const classTargets = Array.from(document.querySelectorAll('[data-anim-class]'));

    const set = new Set([...singles, ...groupChildren, ...classTargets]);
    return Array.from(set);
  };

  const getColumnsCountByLayout = (container, items) => {
    if (!items.length) return 1;

    const top0 = items[0].offsetTop;
    let cols = 0;

    for (const el of items) {
      if (Math.abs(el.offsetTop - top0) <= 2) cols++;
      else break;
    }

    return Math.max(1, cols || 1);
  };

  const setupWaveColumns = (group) => {
    const stagger = toInt(group.dataset.animStagger, 0);
    if (!stagger) return;

    const kids = Array.from(group.querySelectorAll('[data-anim]'));
    if (!kids.length) return;

    const cols = getColumnsCountByLayout(group, kids);
    const rows = Math.ceil(kids.length / cols);

    kids.forEach((el, idx) => {
      if (el.dataset.animDelay && el.dataset.animDelay !== '') return;

      const row = Math.floor(idx / cols);
      const col = idx % cols;

      // col=0: row 0..rows-1, then col=1: row 0..rows-1, ...
      const waveIndex = col * rows + row;

      el.dataset.animDelay = String(waveIndex * stagger);
    });
  };

  const setupStaggerDomOrder = (group) => {
    const stagger = toInt(group.dataset.animStagger, 0);
    if (!stagger) return;

    const kids = Array.from(group.querySelectorAll('[data-anim]'));
    kids.forEach((el, idx) => {
      if (el.dataset.animDelay && el.dataset.animDelay !== '') return;
      el.dataset.animDelay = String(idx * stagger);
    });
  };

  const setupGroupsStaggers = () => {
    const groups = Array.from(document.querySelectorAll('[data-anim-child]'));

    groups.forEach((group) => {
      const kids = Array.from(group.querySelectorAll('[data-anim]'));

      // clear auto delays
      kids.forEach((el) => {
        if (el.dataset.animDelayAuto === '1') {
          el.dataset.animDelay = '';
          delete el.dataset.animDelayAuto;
        }
      });

      const waveMode = (group.dataset.animWave || '').trim();

      // mark "empty delays" as auto so we can recalc on resize
      kids.forEach((el) => {
        if (!el.dataset.animDelay || el.dataset.animDelay === '') {
          el.dataset.animDelayAuto = '1';
        }
      });

      if (waveMode === 'columns') {
        setupWaveColumns(group);
      } else {
        setupStaggerDomOrder(group);
      }
    });
  };

  const init = () => {
    if (!window.matchMedia('(min-width: 768px)').matches) {
      collectTargets().forEach((el) => {
        el.style.opacity = '';
        el.style.willChange = '';
        el.classList.remove('animate__animated');
      });
      return;
    }

    if (prefersReducedMotion()) {
      collectTargets().forEach((el) => {
        el.style.opacity = '';
        el.style.willChange = '';
      });
      return;
    }

    setupGroupsStaggers();

    const targets = collectTargets();
    targets.forEach(prepareElement);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const once = getBool(el.dataset.animOnce, DEFAULTS.once);

          if (entry.isIntersecting) {
            // --- CLASS MODE (transition-based) for blur blocks
            if (el.dataset.animClass) {
              setTransitionVars(el);
              el.classList.add(el.dataset.animClass);

              if (once) {
                io.unobserve(el);
                el.dataset.animPlayed = '1';
              }
              return;
            }

            // --- Animate.css mode
            playAnimation(el);

            if (once) {
              io.unobserve(el);
              el.dataset.animPlayed = '1';
            }
          } else {
            if (!once) {
              clearAnimationClasses(el);

              if (!el.dataset.animClass) {
                el.style.opacity = '0';
              }
            }
          }
        });
      },
      { threshold: DEFAULTS.threshold, rootMargin: DEFAULTS.rootMargin }
    );

    targets.forEach((el) => {
      const once = getBool(el.dataset.animOnce, DEFAULTS.once);
      if (once && el.dataset.animPlayed === '1') return;
      io.observe(el);
    });

    // Recalc wave delays on resize (breakpoints change columns)
    const groups = Array.from(document.querySelectorAll('[data-anim-child]'));
    if (window.ResizeObserver && groups.length) {
      const ro = new ResizeObserver(() => {
        setupGroupsStaggers();
      });
      groups.forEach((g) => ro.observe(g));
    } else {
      window.addEventListener('resize', () => setupGroupsStaggers(), { passive: true });
    }
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
})();